import MuiChip, { ChipProps } from '@material-ui/core/Chip'
import { EventHandler, FC, ReactNode } from 'react'
import styled from '@emotion/styled'
import Typography from '@material-ui/core/Typography'
import { getTestId } from '../test/automationTestUtils'

import Icon from './Icon'
import { useAppTheme } from '../theme/'
import SelectionCountBadge from './SelectionCountBadge'

interface IChipProps extends ChipProps {
    endIcon?: ChipProps['deleteIcon']
    onEndIconClick?: ChipProps['onDelete']
    /**
     * When given, overrides the endIcon and renders an arrow icon in its place.
     */
    isSelectionIconRendered?: boolean
    /**
     * Displays amount of selections, if the chip is related to some selections (e.g.
     * dropdown values).
     */
    selectionCount?: number
    /**
     * When given, reserves the number given in pixels for the selectionCount even if
     * selectionCount is null or zero.
     */
    spaceReservedForSelectionCount?: number | null
    showDeleteIcon?: boolean
    testId?: string
}

const StyledMuiChip = styled(MuiChip, { shouldForwardProp: (propName) => propName !== 'hasSelections' })<{
    hasSelections: boolean
}>`
    &.MuiButtonBase-root {
        border-radius: ${({ theme }) => theme.tokens.radiusDefault};
        border: 1px solid ${({ theme }) => theme.colors.primaryTeal60};
        padding-right: 4px;
    }

    height: 32px;
    ${({ onClick }) =>
        onClick &&
        `
        cursor: pointer;
    `}

    transition: background-color ${({ theme }) => theme.tokens.transitionInstant}, border-color ${({ theme }) =>
        theme.tokens.transitionInstant};
    background-color: ${({ theme }) => theme.colors.neutralsWhite100};

    :hover {
        border-color: transparent;
        background-color: ${({ hasSelections, theme }) =>
            !hasSelections
                ? theme.componentExtensions.buttons.bgButtonPrimaryHover
                : theme.componentExtensions.buttons.bgButtonPrimaryActive};
    }

    :focus {
        ${({ onClick, hasSelections, theme }) =>
            onClick &&
            `
            background-color: ${
                !hasSelections ? theme.colors.neutralsWhite100 : theme.componentExtensions.buttons.bgButtonPrimaryActive
            };

            &.MuiButtonBase-root {
                outline: 2px solid ${theme.colors.primaryTeal100};
                border-color: transparent;
            }
        `}
    }

    :active {
        background-color: ${({ hasSelections, theme }) =>
            !hasSelections ? theme.colors.primaryCyan11 : theme.componentExtensions.buttons.bgButtonPrimaryActive};
        &.MuiButtonBase-root {
            border: ${({ theme }) => `1px solid ${theme.colors.primaryTeal60}`};
        }
    }

    .MuiChip-deleteIcon {
        display: flex;
        color: ${({ theme }) => theme.componentExtensions.buttons.bgButtonPrimaryDefault};
        font-size: 20px;
    }

    &.MuiButtonBase-root .MuiChip-label {
        padding-right: 8px;
    }

    ${({ hasSelections, theme }) =>
        hasSelections &&
        `
        background-color: ${theme.componentExtensions.buttons.bgButtonPrimaryActive};
        
        &.MuiButtonBase-root {
            border-color: transparent;
        }
    `}

    ${({ disabled, theme }) =>
        disabled &&
        `
        background-color: ${theme.componentExtensions.buttons.bgButtonPrimaryDisabled};
        &.MuiButtonBase-root {
            border: 1px solid ${theme.componentExtensions.buttons.bgButtonPrimaryDisabled};
        }
        &.Mui-disabled {
            color: ${theme.colors.neutralsWhite100};
        }
    `}
`

export const StringLabel = styled(Typography)<{ disabled?: boolean }>`
    font-family: Inter, arial;
    color: ${({ theme, disabled }) =>
        disabled ? theme.colors.neutralsWhite100 : theme.componentExtensions.buttons.bgButtonPrimaryDefault};
`

const NodeSelectionCount = styled(SelectionCountBadge)`
    position: absolute;
    right: 0;
    margin-left: 8px;
`

const LabelContainer = styled.div<{ showSelectionCountNode?: boolean }>`
    display: flex;
    align-items: center;
    position: relative;
    padding-right: ${({ showSelectionCountNode }) => `${showSelectionCountNode ? '28px' : 0}`};
`

const StyledIcon = styled(Icon)`
    pointer-events: none;
`

const SelectionCountNode: FC<{
    selectionCount: number | null
    disabled?: boolean
}> = ({ selectionCount }) => {
    if (selectionCount === null || selectionCount === 0) {
        return null
    }

    return <NodeSelectionCount count={selectionCount} testID="chip-selection-count" />
}

interface ILabelProps {
    selectionCount: number | null
    label: ReactNode
    spaceReservedForSelectionCount: number | null
    disabled?: boolean
}

const Label = ({ selectionCount, label, spaceReservedForSelectionCount, disabled }: ILabelProps) => {
    const labelToUse = typeof label === 'string' ? <StringLabel disabled={disabled}>{label}</StringLabel> : label
    const showSelectionCountNode = Boolean(selectionCount || spaceReservedForSelectionCount)

    return (
        <LabelContainer showSelectionCountNode={showSelectionCountNode}>
            {labelToUse}
            {showSelectionCountNode && <SelectionCountNode selectionCount={selectionCount} disabled={disabled} />}
        </LabelContainer>
    )
}

const Chip = ({
    selectionCount = 0,
    onEndIconClick,
    isSelectionIconRendered = false,
    showDeleteIcon = false,
    spaceReservedForSelectionCount = null,
    label,
    onClick,
    disabled,
    testId,
    ...props
}: IChipProps): JSX.Element => {
    const { componentExtensions, colors } = useAppTheme()
    const labelToUse = (
        <Label
            label={label}
            selectionCount={selectionCount ?? null}
            spaceReservedForSelectionCount={spaceReservedForSelectionCount}
            disabled={disabled}
        />
    )

    const getEndIcon = () => {
        if (isSelectionIconRendered) {
            return (
                <StyledIcon size={20} color={disabled ? colors.neutralsWhite100 : componentExtensions.icons.iconAction}>
                    expand_more
                </StyledIcon>
            )
        } else if (showDeleteIcon) {
            return (
                <button type="button">
                    <Icon size={16} color={disabled ? colors.neutralsWhite100 : componentExtensions.icons.iconAction}>
                        clear
                    </Icon>
                </button>
            )
        }

        return undefined
    }

    const endIconToUse = getEndIcon()
    const labelIsDateRange = /[0-9][0-9.]*[0-9]/.test(label as string)

    const testIdToUse =
        getTestId(
            ['VALUE_PICKER', 'CHIP', 'DROPDOWN'],
            labelIsDateRange ? 'DATE_RANGE' : typeof label === 'string' ? label : undefined
        ) ?? testId

    const handleDelete: EventHandler<any> = (e) => {
        if (onEndIconClick) {
            onEndIconClick(e)
        } else if (onClick) {
            onClick(e)
        }
    }

    return (
        <StyledMuiChip
            deleteIcon={endIconToUse}
            label={labelToUse}
            onClick={onClick}
            onDelete={endIconToUse && handleDelete}
            hasSelections={selectionCount !== 0 && !labelIsDateRange}
            {...props}
            disabled={disabled}
            data-testid={testIdToUse}
        />
    )
}

export default Chip
